import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useReleases } from '../../hooks/useReleaseChecklist';
import { ReleaseManagement } from './ReleaseManagement';
import { ChecklistDetail } from './ChecklistDetail';
import { Filters, FilterChip } from './ReleaseManagement/Filters';
import {
  HashState,
  buildHash,
  hashEqual,
  readHash,
} from './hash';
import { FloatingReleasePanel } from './FloatingReleasePanel';
import { RELEASE_COMPACT_MAX_WIDTH_PX } from './layoutBreakpoints';

export const ReleaseChecklistFetch = () => {
  const initial = useMemo(() => readHash(), []);
  const [release, setRelease] = useState<string | null>(initial.release);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(initial.owner);
  const [stateFilter, setStateFilter] = useState<Set<FilterChip>>(
    initial.state,
  );

  // Match CompactSidebarPage: below this width the left "Manage releases"
  // column becomes a centered floating slide-down so tables use full width.
  const isCompact = useMediaQuery(
    `(max-width:${RELEASE_COMPACT_MAX_WIDTH_PX}px)`,
    { noSsr: true },
  );

  // Reset filters when the user picks a different release — old filter values
  // (owners that don't exist in the new release, etc.) would silently hide
  // everything otherwise.
  const resetFilters = useCallback(() => {
    setOwnerFilter(null);
    setStateFilter(new Set());
  }, []);

  const { data: releases, error, isFetching } = useReleases();

  // Sync URL hash → state for back/forward navigation and external links.
  // The listener reads ALL fields (release + owner + state) so filter
  // selections survive a browser back-button trip.
  useEffect(() => {
    const onHashChange = () => {
      const next = readHash();
      setRelease(next.release);
      setOwnerFilter(next.owner);
      setStateFilter(next.state);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Mirror state → hash. Release changes push a new history entry (so back
  // walks the user's release history). Filter changes only replace the
  // current entry — otherwise every chip toggle would pollute history.
  // The first effect run after mount uses replace, since the hash already
  // reflects the initial state.
  const lastHashRef = React.useRef<HashState>(initial);
  const firstSyncRef = React.useRef(true);
  useEffect(() => {
    const current: HashState = {
      release,
      owner: ownerFilter,
      state: stateFilter,
    };
    if (hashEqual(current, lastHashRef.current)) return;
    const releaseChanged = current.release !== lastHashRef.current.release;
    lastHashRef.current = current;
    const wanted = buildHash(current);
    if (window.location.hash === wanted) return;
    const url = window.location.pathname + window.location.search + wanted;
    if (firstSyncRef.current) {
      firstSyncRef.current = false;
      window.history.replaceState(null, '', url);
    } else if (releaseChanged) {
      window.history.pushState(null, '', url);
    } else {
      window.history.replaceState(null, '', url);
    }
  }, [release, ownerFilter, stateFilter]);

  const handleSelect = useCallback(
    (next: string) => {
      setRelease(next || null);
      resetFilters();
    },
    [resetFilters],
  );

  const managementBody = isFetching && !releases ? (
    <Progress />
  ) : error ? (
    <ResponseErrorPanel
      error={error}
      title="Fetching releases"
      defaultExpanded
    />
  ) : (
    <ReleaseManagement
      releases={releases ?? []}
      selectedRelease={release}
      onSelect={handleSelect}
    >
      <Filters
        release={release}
        ownerFilter={ownerFilter}
        setOwnerFilter={setOwnerFilter}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
      />
    </ReleaseManagement>
  );

  const checklist =
    release !== null ? (
      <ChecklistDetail
        release={release}
        ownerFilter={ownerFilter}
        stateFilter={stateFilter}
      />
    ) : (
      <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>
        {isCompact
          ? 'Open Manage releases above to pick or create a release.'
          : 'Pick a release on the left, or create a new one.'}
      </div>
    );

  if (isCompact) {
    return (
      <>
        <FloatingReleasePanel selectedRelease={release}>
          {managementBody}
        </FloatingReleasePanel>
        {checklist}
      </>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item md={3} lg={2}>
        {/* Stick the release picker to the top so it stays in view while
            the (potentially very long) checklist on the right scrolls.
            Grid items default to `align-items: stretch`, which gives this
            left column the full container height — exactly the scroll
            runway `position: sticky` needs. */}
        <div style={{ position: 'sticky', top: 16 }}>{managementBody}</div>
      </Grid>
      <Grid item md={9} lg={10}>
        {checklist}
      </Grid>
    </Grid>
  );
};
